import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Navbar } from '../components/Navbar'
import { Footer } from '../components/Footer'
import { Button } from '../components/ui/Button'
import { Input } from '../components/ui/Input'
import { Select } from '../components/ui/Select'
import { Textarea } from '../components/ui/Textarea'
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card'
import { Stepper } from '../components/ui/Stepper'
import { MicroText } from '../components/ui/MicroText'
import {Droplet, ArrowLeft, Share2, AlertCircle, Shield, Mail, Phone, Image as ImageIcon, Upload} from 'lucide-react'
import { lumi } from '../lib/lumi'
import toast from 'react-hot-toast'
import { useAuth } from '../hooks/useAuth'
import { sendRequestConfirmationEmail, sendDonorMatchEmail } from '../services/emailService'
import { analytics } from '../utils/analytics'
import { compressImage } from '../utils/imageOptimization'

const bloodTypes = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-']
const states = ['AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MT', 'MS', 'MG', 'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN', 'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO']
const urgencyLevels = [
  { value: 'critical', label: 'Crítico - Urgente', color: 'text-red-600' },
  { value: 'high', label: 'Alta - Importante', color: 'text-orange-600' },
  { value: 'medium', label: 'Média - Programado', color: 'text-blue-600' }
]

function generateShareToken() {
  return Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15)
}

export function NewRequest() {
  const navigate = useNavigate()
  const { user } = useAuth()
  const [loading, setLoading] = useState(false)
  
  const [formData, setFormData] = useState({
    patientName: '',
    bloodType: '',
    hospital: '',
    city: '',
    state: '',
    urgency: 'high',
    unitsNeeded: 1,
    description: '',
    contactEmail: '',
    contactPhone: '',
    acceptsCommunicationEmail: true,
    acceptsCommunicationWhatsapp: true,
    patientPhoto: ''
  })
  const [uploadingPhoto, setUploadingPhoto] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    // Validação de campos obrigatórios
    if (!formData.patientName || !formData.bloodType || !formData.hospital || 
        !formData.city || !formData.state || (!formData.contactEmail && !formData.contactPhone)) {
      toast.error('Preencha todos os campos obrigatórios')
      return
    }

    // Validação de email ou telefone
    if (!formData.contactEmail && !formData.contactPhone) {
      toast.error('Informe ao menos um meio de contato (e-mail ou WhatsApp)')
      return
    }

    setLoading(true)
    try {
      const shareToken = generateShareToken()
      const requestData = {
        ...formData,
        userId: user?.userId || null,
        status: 'active',
        unitsNeeded: Number(formData.unitsNeeded),
        shareToken,
        isAnonymous: !user,
        patientPhoto: formData.patientPhoto || undefined
      }

      const newRequest = await lumi.entities.requests.create(requestData)
      
      // Track request creation
      analytics.trackRequestCreation(
        formData.bloodType,
        formData.urgency,
        formData.city,
        !user
      )
      
      // Enviar email de confirmação se houver email
      if (formData.contactEmail) {
        try {
          await sendRequestConfirmationEmail({
            requestId: newRequest._id,
            patientName: formData.patientName,
            bloodType: formData.bloodType,
            hospital: formData.hospital,
            city: formData.city,
            state: formData.state,
            contactEmail: formData.contactEmail,
            shareToken
          })
          toast.success('Seu pedido está no ar! Agora vamos buscar pessoas compatíveis na sua região. Fique atento ao seu e-mail e WhatsApp.')
        } catch (emailError) {
          console.error('Email error:', emailError)
          toast.success('Seu pedido está no ar! Agora vamos buscar pessoas compatíveis na sua região.')
        }
      } else {
        toast.success('Seu pedido está no ar! Agora vamos buscar pessoas compatíveis na sua região.')
      }
      
      // Redirecionar para página de agradecimento
      setTimeout(() => {
        navigate(`/obrigado/${newRequest._id}`)
      }, 1500)
    } catch (error) {
      toast.error('Erro ao criar solicitação. Tente novamente.')
      console.error(error)
    } finally {
      setLoading(false)
    }
  }

  const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Por favor, selecione uma imagem válida')
      return
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('A imagem deve ter no máximo 5MB')
      return
    }

    setUploadingPhoto(true)
    try {
      // Compress image to max 500KB
      const compressedFile = await compressImage(file, 500)
      const result = await lumi.tools.file.upload([compressedFile])
      if (result[0]?.fileUrl) {
        setFormData({ ...formData, patientPhoto: result[0].fileUrl })
        toast.success('Foto enviada com sucesso!')
      } else {
        throw new Error('Upload failed')
      }
    } catch (error) {
      console.error('Photo upload error:', error)
      toast.error('Erro ao enviar foto. Tente novamente.')
    } finally {
      setUploadingPhoto(false)
    }
  }

  const handleRemovePhoto = () => {
    setFormData({ ...formData, patientPhoto: '' })
    toast.success('Foto removida')
  }

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      <div className="container mx-auto px-4 py-6 pb-20 max-w-2xl">
        <Button 
          variant="ghost" 
          onClick={() => navigate('/')}
          className="mb-4"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Voltar
        </Button>

        <Card className="border border-red-200 shadow-lg">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 rounded-full bg-red-600 flex items-center justify-center">
                <Droplet className="h-8 w-8 text-white fill-current" />
              </div>
            </div>
            <CardTitle className="text-2xl">Estou precisando de doação de sangue</CardTitle>
            <p className="text-sm text-gray-600 mt-2">
              Se você chegou até aqui, é porque você ou alguém que ama está passando por um momento difícil. Vamos tentar deixar essa parte menos pesada.
            </p>
            <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-2">
              <p className="text-sm text-gray-700 font-medium">Como funciona:</p>
              <ul className="text-xs text-gray-600 space-y-1">
                <li>✓ Você cria um pedido com tipo sanguíneo, cidade e urgência</li>
                <li>✓ A gente mostra para doadores compatíveis da sua região</li>
                <li>✓ Você recebe contatos de pessoas dispostas a ajudar</li>
              </ul>
            </div>
            <div className="mt-3 text-center">
              <p className="text-sm text-gray-700 font-medium">
                Você não está sozinho. Existem pessoas prontas para ajudar – e nosso papel é aproximar vocês.
              </p>
            </div>
            {!user && (
              <div className="mt-3">
                <MicroText variant="info" icon={AlertCircle}>
                  Você pode criar um pedido sem cadastro. Enviaremos um e-mail com o link para acompanhar.
                </MicroText>
              </div>
            )}
          </CardHeader>
          
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label htmlFor="patientName" className="block text-sm font-medium text-gray-700 mb-1">
                  Nome do Paciente <span className="text-red-600">*</span>
                </label>
                <Input
                  id="patientName"
                  type="text"
                  placeholder="Nome completo de quem precisa"
                  value={formData.patientName}
                  onChange={(e) => setFormData({ ...formData, patientName: e.target.value })}
                  required
                  aria-required="true"
                />
              </div>

              <div>
                <label htmlFor="patientPhoto" className="block text-sm font-medium text-gray-700 mb-1">
                  Foto do Paciente (Opcional)
                </label>
                <div className="space-y-2">
                  {formData.patientPhoto ? (
                    <div className="relative inline-block">
                      <img 
                        src={formData.patientPhoto} 
                        alt="Foto do paciente" 
                        className="w-32 h-32 rounded-lg object-cover border-2 border-red-200"
                      />
                      <button
                        type="button"
                        onClick={handleRemovePhoto}
                        className="absolute -top-2 -right-2 bg-red-600 text-white rounded-full w-6 h-6 flex items-center justify-center hover:bg-red-700"
                      >
                        ×
                      </button>
                    </div>
                  ) : (
                    <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-red-400 hover:bg-red-50 transition-colors">
                      <div className="flex flex-col items-center justify-center pt-5 pb-6">
                        {uploadingPhoto ? (
                          <div className="text-gray-500 text-sm">Enviando...</div>
                        ) : (
                          <>
                            <Upload className="h-8 w-8 text-gray-400 mb-2" />
                            <p className="text-sm text-gray-600 font-medium">Clique para adicionar foto</p>
                            <p className="text-xs text-gray-500 mt-1">PNG, JPG até 5MB</p>
                          </>
                        )}
                      </div>
                      <input
                        id="patientPhoto"
                        type="file"
                        className="hidden"
                        accept="image/*"
                        onChange={handlePhotoUpload}
                        disabled={uploadingPhoto}
                        aria-label="Upload foto do paciente"
                      />
                    </label>
                  )}
                  <p className="text-xs text-gray-500">
                    💡 Uma foto ajuda a humanizar o pedido e aumenta as chances de doação
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="bloodType" className="block text-sm font-medium text-gray-700 mb-1">
                    Tipo Sanguíneo <span className="text-red-600">*</span>
                  </label>
                  <Select
                    id="bloodType"
                    value={formData.bloodType}
                    onChange={(e) => setFormData({ ...formData, bloodType: e.target.value })}
                    required
                    aria-required="true"
                  >
                    <option value="">Selecione</option>
                    {bloodTypes.map(type => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </Select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Unidades Necessárias
                  </label>
                  <Stepper
                    value={formData.unitsNeeded}
                    onChange={(value) => setFormData({ ...formData, unitsNeeded: value })}
                    min={1}
                    max={10}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nível de Urgência <span className="text-red-600">*</span>
                </label>
                <Select
                  value={formData.urgency}
                  onChange={(e) => setFormData({ ...formData, urgency: e.target.value })}
                  required
                >
                  {urgencyLevels.map(level => (
                    <option key={level.value} value={level.value}>{level.label}</option>
                  ))}
                </Select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Hospital / Clínica <span className="text-red-600">*</span>
                </label>
                <Input
                  type="text"
                  placeholder="Nome do hospital ou clínica"
                  value={formData.hospital}
                  onChange={(e) => setFormData({ ...formData, hospital: e.target.value })}
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Cidade <span className="text-red-600">*</span>
                  </label>
                  <Input
                    type="text"
                    placeholder="Cidade"
                    value={formData.city}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Estado <span className="text-red-600">*</span>
                  </label>
                  <Select
                    value={formData.state}
                    onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                    required
                  >
                    <option value="">UF</option>
                    {states.map(state => (
                      <option key={state} value={state}>{state}</option>
                    ))}
                  </Select>
                </div>
              </div>

              <div className="border-t pt-4">
                <h3 className="text-sm font-bold text-gray-900 mb-3 flex items-center">
                  <Mail className="h-4 w-4 mr-2 text-red-600" />
                  Informações de Contato <span className="text-red-600 ml-1">*</span>
                </h3>
                
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      E-mail para Contato
                    </label>
                    <Input
                      type="email"
                      placeholder="seu@email.com"
                      value={formData.contactEmail}
                      onChange={(e) => setFormData({ ...formData, contactEmail: e.target.value })}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Enviaremos confirmação e link para compartilhar
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      WhatsApp
                    </label>
                    <Input
                      type="tel"
                      placeholder="(00) 00000-0000"
                      value={formData.contactPhone}
                      onChange={(e) => setFormData({ ...formData, contactPhone: e.target.value })}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Para doadores entrarem em contato diretamente
                    </p>
                  </div>
                </div>

                <div className="mt-3 space-y-2">
                  <label className="flex items-center space-x-2 text-sm">
                    <input
                      type="checkbox"
                      checked={formData.acceptsCommunicationEmail}
                      onChange={(e) => setFormData({ ...formData, acceptsCommunicationEmail: e.target.checked })}
                      className="rounded border-gray-300 text-red-600 focus:ring-red-500"
                    />
                    <span className="text-gray-700">Aceito receber notificações por e-mail</span>
                  </label>
                  <label className="flex items-center space-x-2 text-sm">
                    <input
                      type="checkbox"
                      checked={formData.acceptsCommunicationWhatsapp}
                      onChange={(e) => setFormData({ ...formData, acceptsCommunicationWhatsapp: e.target.checked })}
                      className="rounded border-gray-300 text-red-600 focus:ring-red-500"
                    />
                    <span className="text-gray-700">Aceito receber notificações por WhatsApp (futuro)</span>
                  </label>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Descrição / Observações
                </label>
                <Textarea
                  placeholder="Compartilhe apenas o necessário para que alguém possa ir até o hemocentro te ajudar. Não precisa expor detalhes íntimos."
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={4}
                />
                <p className="text-xs text-gray-500 mt-1">
                  Exemplo: "Minha mãe precisa de cirurgia e está aguardando doadores há 3 dias"
                </p>
              </div>

              <div className="bg-white border border-red-200 rounded-lg p-4 flex items-start space-x-3">
                <Share2 className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-gray-700">
                  <p className="font-medium text-gray-900 mb-1">Compartilhe para alcançar mais doadores</p>
                  <p className="text-xs">
                    Após criar a solicitação, você receberá um link para compartilhar no WhatsApp, Facebook, Instagram e outras redes sociais.
                  </p>
                </div>
              </div>

              <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 flex items-start space-x-2">
                <Shield className="h-4 w-4 text-gray-600 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-gray-600">
                  Seus dados são usados apenas para conectar doadores e receptores de forma responsável. Não compartilhamos com terceiros.
                </p>
              </div>

              <Button 
                type="submit" 
                className="w-full h-12 text-base font-bold bg-red-600 hover:bg-red-700 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={loading || uploadingPhoto}
              >
                {loading ? (
                  <span className="flex items-center justify-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Buscando doadores compatíveis...
                  </span>
                ) : uploadingPhoto ? (
                  <span className="flex items-center justify-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Enviando foto...
                  </span>
                ) : '🩸 Criar meu pedido de ajuda'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
      <Footer />
    </div>
  )
}
